// Mirrors backend services/weekly_reports.py — keep these two in sync.
export const RESOURCE_CATEGORIES = ["Site Management Team", "Direct Labour", "Subcontractor Labour", "Specialists / Visitors"];

export function setIn(obj, path, value) {
    const keys = path.split(".");
    const root = { ...obj };
    let cur = root;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur[k] = { ...(cur[k] || {}) };
        cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
    return root;
}
export function getIn(obj, path, fallback) {
    const v = path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
    return v === undefined ? fallback : v;
}

export const OVERVIEW_ITEMS = [
    { key: "time", label: "Time", sub: "Current programme position", options: ["On plan", "Watch", "Delay"] },
    { key: "safety", label: "Safety", sub: "Weekly H&S position", options: ["No incidents", "Actions open"] },
    { key: "quality", label: "Quality", sub: "Inspection outcome", options: ["Pass", "NCRs open"], optionLabels: { "NCRs open": "Non-conformances open" } },
    { key: "risk", label: "Risk", sub: "Most material constraint", options: ["Access", "Info", "Weather"] },
    { key: "labour", label: "Labour", sub: "Average weekly headcount", options: ["Adequate", "Shortfall"] },
    { key: "plant", label: "Plant", sub: "Critical plant", options: ["Available", "Constrained"] },
    { key: "information", label: "Information", sub: "Outstanding approvals / RFIs", options: ["Received", "Required"] },
    { key: "next_week", label: "Next week", sub: "Immediate focus", options: ["Mobilise", "Recover", "Complete"] },
];

export const QUALITY_CHECKLIST_ITEMS = ["ITPs available", "Records complete", "Snags / defects outstanding", "NCR raised this week"];
// Display-only relabelling (keys above never change, so stored answers keep working) —
// kept in sync with backend services/weekly_reports.py::QUALITY_CHECKLIST_LABELS.
export const QUALITY_CHECKLIST_LABELS = {
    "ITPs available": "Inspection & Test Plans (ITPs) available",
    "NCR raised this week": "Non-Conformance Report (NCR) raised this week",
};

export const HS_CHECKLIST_ITEMS = [
    "Site inspections completed", "Site Setup", "Welfare Facilities", "Housekeeping",
    "H&S Management", "Scaffold Inspection", "Mobile Towers", "COSHH", "PPE",
    "Manual Handling", "Noise & Vibration", "Gas Safety", "Fire Safety",
    "Electrical Safety", "PAT Testing", "Asbestos Management", "Permits / RAMS in place",
    "Accident / Near Miss", "Working at Height", "Traffic Management & Street Works",
    "Temporary Works", "First Aid",
];

export const ENVIRONMENTAL_CHECKLIST_ITEMS = [
    "Waste segregation in place", "Dust / noise controls in place", "Spill kits / protection in place", "Environmental complaint this week",
];

// The 8 items Generate auto-fills with evidence (item C, Prompt 4) — the other
// 18 across both checklists stay manual. Kept in sync with backend
// services/weekly_reports.py::_suggest_hs_checklist.
export const HS_AUTO_ITEMS = [
    "Site inspections completed", "Scaffold Inspection", "Accident / Near Miss",
    "Permits / RAMS in place", "Traffic Management & Street Works", "Fire Safety",
    "Asbestos Management", "Environmental complaint this week",
];

export const CDM_DUTY_ITEMS = [
    "Pre-start checks completed", "RAMS reviewed & briefed", "Permits issued",
    "Temporary works inspected", "Daily site inspections completed",
    "Subcontractor competence verified", "Workfront released safely",
];

export const INDUCTIONS_CHECKLIST_ITEMS = ["New starters inducted", "Visitors inducted", "Subcontractors inducted"];

export const CHECK_VALUES = ["Yes", "No", "N/A"];
export const RAG_VALUES = ["Green", "Amber", "Red"];
// Oct 2026 Section 3.0 rework — Delay Cause is now a reason dropdown
// (required only when behind schedule), no "None" option needed.
export const DELAY_REASON_OPTIONS = ["Access", "Info", "Labour", "Plant", "Weather", "Subcontractor"];
export const SCORE_OPTIONS = ["1", "2", "3", "4", "5"];
export const IMPACT_OPTIONS = ["Low", "Medium", "High"];
// Oct 2026 feedback Prompt 6 — Open -> Closed-awaiting sign-off (site manager,
// requires a closure note) -> Closed (reviewer-approved, final).
export const RISK_STATUS_OPTIONS = ["Open", "Closed - awaiting sign-off", "Closed"];
export const PLANT_OWNER_OPTIONS = ["Direct", "Hire"];
export const PLANT_STATUS_OPTIONS = ["In use", "Standby", "Broken down", "Off hire"];
export const RESPONSIBLE_PARTY_OPTIONS = [
    { value: "client", label: "Client" }, { value: "pr", label: "PR" }, { value: "shared", label: "Shared" },
];
export const INSPECTION_STATUS_OPTIONS = ["Open", "Closed"];

export const SIGN_OFF_DECLARATION = "I confirm all required inspections, RAMS briefings, permits, and compliance checks were completed this week. I also confirm that a toolbox talk was delivered, recorded, and fully briefed to all operatives, with attendance captured and key safety messages reinforced, and I have reviewed all information pre-populated by the system.";

export const SENIOR_REVIEW_STATUS_OPTIONS = [
    { value: "reviewed", label: "Reviewed" },
    { value: "accepted", label: "Accepted" },
    { value: "returned", label: "Comments issued — return to Site Manager" },
];

export const NARRATIVE_HINTS = {
    exec_summary: "Summarise the week in plain terms: what got done, what's at risk, and what the client needs to know. Avoid repeating the tables below.",
};

export const RAG_COLORS = {
    Green: { bg: "#E4F1E6", fg: "#2E7D32", border: "#BFDFC4" },
    Amber: { bg: "#FDF0DC", fg: "#B5720A", border: "#F2DAAD" },
    Red: { bg: "#FBE3E1", fg: "#B3261E", border: "#F3C6C2" },
};

export function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
}

export function fmtDateTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function fmtReportingWeek(weekStart, weekEnd) {
    if (!weekStart || !weekEnd) return "—";
    try {
        const s = new Date(weekStart), e = new Date(weekEnd);
        const short = (d) => `${SHORT_DAYS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")} ${SHORT_MONTHS[d.getMonth()]}`;
        const endFull = `${short(e)} ${e.getFullYear()}`;
        return `Week ending ${endFull} (${short(s)} - ${short(e)})`;
    } catch { return `${weekStart} to ${weekEnd}`; }
}

export function inductionCountSummary(inductionLog) {
    const rows = inductionLog || [];
    if (!rows.length) return "No inductions recorded this week";
    const buckets = { "new starters": 0, "visitors": 0, "sub-contractors": 0 };
    rows.forEach((r) => {
        const pt = r.person_type || "";
        if (pt === "Subcontractor") buckets["sub-contractors"]++;
        else if (pt === "Visitor" || pt === "Client") buckets["visitors"]++;
        else buckets["new starters"]++;
    });
    const parts = Object.entries(buckets).filter(([, n]) => n).map(([label, n]) => `${n} ${label}`);
    return `${rows.length} inducted this week - ${parts.join(", ")}`;
}

export function isOverdue(dateStr) {
    if (!dateStr) return false;
    try { return new Date(dateStr) < new Date(new Date().toDateString()); } catch { return false; }
}

export const INVERTED_CHECKLIST_ITEMS = new Set(["Accident / Near Miss", "Environmental complaint this week", "Snags / defects outstanding", "NCR raised this week"]);
export function isAdverseAnswer(label, value) {
    return value === (INVERTED_CHECKLIST_ITEMS.has(label) ? "Yes" : "No");
}

// Mirrors backend services/weekly_reports.py::risk_age_days / risk_age_tone — Prompt 6.
export function riskAgeDays(firstRaisedAt) {
    if (!firstRaisedAt) return null;
    try {
        const start = new Date(firstRaisedAt);
        const days = Math.floor((new Date().setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0)) / 86400000);
        return Math.max(0, days);
    } catch { return null; }
}
export function riskAgeTone(days) {
    if (days === null || days === undefined) return "neutral";
    if (days >= 28) return "red";
    if (days >= 14) return "amber";
    return "neutral";
}

// Mirrors backend services/weekly_reports.py::programme_variance/variance_tone/is_behind_schedule.
export function programmeVariance(pva) {
    const planned = parseFloat(String(pva?.planned_pct ?? "").replace("%", ""));
    const actual = parseFloat(String(pva?.actual_pct ?? "").replace("%", ""));
    if (Number.isNaN(planned) || Number.isNaN(actual)) return null;
    return Math.round((actual - planned) * 10) / 10;
}
export function varianceTone(variance) {
    if (variance === null || variance === undefined) return "neutral";
    return variance >= 0 ? "green" : "red";
}
export function isBehindSchedule(pva) {
    const v = programmeVariance(pva);
    return v !== null && v < 0;
}

// Mirrors backend services/weekly_reports.py::validate_submit — same rules, same order.
// Used for pre-submit client-side highlighting; the backend call is the source of truth.
export function validateWsrSubmit(content, recoveryActionsCount = 0) {
    const issues = [];
    const need = (section, cond, message) => { if (!cond) issues.push({ section, message }); };

    const overview = content.overview || {};
    const unconfirmed = OVERVIEW_ITEMS.filter((it) => !overview[it.key]?.selected);
    need("1.0", unconfirmed.length === 0, `Confirm all 8 Overview tiles (${unconfirmed.length} unconfirmed)`);

    need("2.0", (content.exec_summary?.key_achievements || "").trim(), "Key achievements is required");

    const pva = content.programme?.planned_vs_actual || {};
    need("3.0", (pva.planned_pct ?? "") !== "" && (pva.actual_pct ?? "") !== "", "Planned vs Actual — both Planned % and Actual % are required");
    const rag = content.programme?.rag || {};
    const missingRag = ["time", "cost", "information"].filter((k) => !rag[k]?.rag);
    if (missingRag.length) issues.push({ section: "3.0", message: `Set a RAG rating for ${missingRag.join(", ")}` });
    ["time", "cost", "information"].forEach((k) => {
        const entry = rag[k] || {};
        if (["amber", "red"].includes(entry.rag) && !(entry.note || "").trim()) {
            issues.push({ section: "3.0", message: `${k[0].toUpperCase()}${k.slice(1)} is ${entry.rag} — commentary is required` });
        }
    });
    if (isBehindSchedule(pva)) {
        const delay = content.programme?.delay || {};
        const impact = content.programme?.programme_impact || {};
        if (!(delay.reason || "").trim()) issues.push({ section: "3.0", message: "Behind schedule — a Delay reason is required" });
        if (!(delay.note || "").trim()) issues.push({ section: "3.0", message: "Behind schedule — the Delay root cause note is required" });
        const daysLostRaw = (impact.days_lost || "").trim();
        if (!daysLostRaw) {
            issues.push({ section: "3.0", message: "Behind schedule — Programme Impact (days lost) is required" });
        } else if (parseFloat(daysLostRaw) > 0 && !(impact.notes || "").trim()) {
            issues.push({ section: "3.0", message: "Days lost recorded — Programme Impact notes are required" });
        }
        if (recoveryActionsCount < 1) issues.push({ section: "3.0", message: "Behind schedule — at least one Recovery Action (linked or created) is required" });
    }

    const plant = content.plant || {};
    need("5.0", (plant.items || []).length > 0 || plant.no_plant_on_site, "Tick 'No plant or equipment on site this week', or add at least one item");

    const qualityChecklist = content.quality?.checklist || {};
    const unansweredQ = QUALITY_CHECKLIST_ITEMS.filter((i) => !qualityChecklist[i]?.value);
    need("6.0", unansweredQ.length === 0, `Answer every Quality checklist item (${unansweredQ.length} remaining)`);

    [
        ["6.0", qualityChecklist], ["7.0", content.health_safety?.checklist || {}],
        ["7.0", content.health_safety?.environmental_checklist || {}],
        ["8.0", content.cdm?.duty_checklist || {}], ["9.0", content.inductions?.checklist || {}],
    ].forEach(([section, checklist]) => {
        Object.entries(checklist).forEach(([label, entry]) => {
            if (isAdverseAnswer(label, entry?.value) && !(entry.note || "").trim()) issues.push({ section, message: `'${label}' is answered ${entry.value} — a note is required` });
        });
    });

    (content.cdm?.site_control || []).forEach((row) => {
        const options = row.options || [];
        if (options.length && row.position && row.position !== options[0] && !(row.action_owner || "").trim()) {
            issues.push({ section: "8.0", message: `'${row.area}' is not Normal — Action / owner is required` });
        }
    });
    (content.cdm?.critical_actions || []).forEach((row) => {
        if (row.status === "Pending" && !(row.owner_due || "").trim()) {
            issues.push({ section: "8.0", message: `'${row.action}' is Pending — Owner / due is required` });
        }
    });

    return issues;
}
